import { useEffect, useState } from 'react';
import { apiService } from '../services/api';

/**
 * Opt-in for phone/desktop notifications.
 *
 * Push is strictly additive: every notification is already a row in the
 * list below this control, and turning this off only stops the device
 * alert. Nothing here is required to use the app.
 */

// A VAPID key travels as base64url text but subscribing wants raw bytes.
// Returns the ArrayBuffer rather than the view: applicationServerKey is
// typed BufferSource, and since TypeScript 5.7 made Uint8Array generic
// over its backing buffer, the view no longer satisfies that.
function urlBase64ToBytes(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes.buffer;
}

const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

// iOS only delivers web push to a PWA added to the Home Screen — in a
// Safari tab PushManager isn't there at all. Detecting the platform lets
// us say why rather than silently hiding the option.
const isIos = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone =
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true);

const PushNotificationToggle = () => {
  const [available, setAvailable] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!supported) return;
      try {
        // Server-side switch first: no keys configured means no opt-in.
        const { enabled } = await apiService.getPushPublicKey();
        if (cancelled || !enabled) return;
        setAvailable(true);

        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (!cancelled) setSubscribed(Boolean(existing));
      } catch {
        // Leave the control hidden rather than showing a broken toggle.
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError(
          permission === 'denied'
            ? 'Notifications are blocked for this site. Allow them in your browser settings, then try again.'
            : 'Notifications were not allowed.'
        );
        return;
      }

      const { publicKey } = await apiService.getPushPublicKey();
      if (!publicKey) {
        setError('Notifications are not configured on the server yet.');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      // Reuse whatever this browser already has rather than creating a
      // second subscription for the same device.
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          // Required by every current browser: each push must result in
          // a notification the user can see.
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToBytes(publicKey)
        }));

      await apiService.subscribeToPush(subscription.toJSON());
      setSubscribed(true);
      setMessage('Notifications are on for this device.');
    } catch (err: any) {
      // By this point permission was granted and the server's key was
      // fetched, so a failure here is the browser's own push
      // registration (commonly "Registration failed - push service
      // error" when the device or network can't reach the push service
      // — FCM/Google — or background/battery limits block it) or the
      // server save. The raw browser message is cryptic and alarming, so
      // show something plain and reassuring; the real detail goes to the
      // console for anyone debugging.
      console.error('[Push] Could not enable notifications on this device', err);
      setError(
        "This device couldn't connect to the notification service, so push alerts can't be turned on here right now. " +
          'Your notifications still appear in the list below either way. Try again later or on a different network, ' +
          "and check that this device isn't blocking background activity for your browser."
      );
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Tell the server first — if unsubscribing locally succeeded but
        // the server still held the row, it would keep sending to an
        // endpoint this device no longer listens on.
        await apiService.unsubscribeFromPush(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      setMessage('Notifications are off for this device.');
    } catch (err: any) {
      setError(err?.message || 'Could not turn off notifications.');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await apiService.sendTestPush();
      setMessage('Test sent — it should appear shortly.');
    } catch (err: any) {
      setError(err?.message || 'Could not send a test notification.');
    } finally {
      setBusy(false);
    }
  };

  // On iPhone the API only exists once the app is installed, so explain
  // that instead of leaving the section mysteriously absent.
  if (!supported) {
    if (isIos && !isStandalone) {
      return (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3>Phone Notifications</h3>
          </div>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            On iPhone and iPad, notifications work only after you add this site to your Home Screen. In Safari, tap
            the Share button, choose <strong>Add to Home Screen</strong>, then open it from there and come back to
            this page.
          </p>
        </div>
      );
    }
    return null;
  }

  if (!available) return null;

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div className="card-header">
        <h3>Phone Notifications</h3>
      </div>

      {error && (
        <div className="alert-error" style={{ padding: '0.75rem', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      {message && (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#34D399' }}>{message}</p>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}
      >
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', flex: '1 1 18rem', minWidth: 0 }}>
          {subscribed
            ? 'This device will get an alert when there is an update on your application, a new announcement, or a new program.'
            : 'Get an alert on this device when there is an update on your application, a new announcement, or a new program. Everything still appears in the list below either way.'}
        </p>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {subscribed ? (
            <>
              <button className="btn btn-outline btn-sm" type="button" disabled={busy} onClick={sendTest}>
                Send a test
              </button>
              <button className="btn btn-outline btn-sm" type="button" disabled={busy} onClick={disable}>
                {busy ? 'Working...' : 'Turn off'}
              </button>
            </>
          ) : (
            <button className="btn btn-primary btn-sm" type="button" disabled={busy} onClick={enable}>
              {busy ? 'Working...' : 'Turn on notifications'}
            </button>
          )}
        </div>
      </div>

      {/* Each browser subscribes separately, which is not obvious. */}
      <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        This setting applies to this device only — turn it on again on any other phone or computer you use.
      </p>
    </div>
  );
};

export default PushNotificationToggle;
